import { Input as MuiInput } from '@mui/material';
import { InputAdornment, TextareaAutosize } from '@mui/material';
import { useRef, useState } from 'react';

import { cn } from '../../../utils/tailwind.utils';
import { Size, TextIconColor } from '../constants';
import { Icon, Icons } from '../Icon';
import {
  Typography,
  TypographyFont,
  TypographySize,
  TypographyWeight
} from '../Typography';

import { TextFieldType } from './TextField.consts';
import { textField } from './TextField.styles';

type RootProps = {
  [key: string]: any;
  children?: React.ReactNode;
  className?: string;
};

const Root = ({ className, ...props }: RootProps) => (
  <div className={className} {...props}>
    {props.children}
  </div>
);

type InputProps = {
  [key: string]: any;
  className?: string;
};

const Input = ({ className, ...props }: InputProps) => (
  <input className={className} {...props} />
);

type TextAreaStyledProps = {
  [key: string]: any;
  className?: string;
};

const TextAreaStyled = ({ className, ...props }: TextAreaStyledProps) => (
  <TextareaAutosize className={className} {...props} />
);

interface TextFieldProps {
  onChange: (value: string) => void;
  value: string;
  animateStartIcon?: boolean;
  autoFocus?: boolean;
  avoidLayoutShift?: boolean;
  disabled?: boolean;
  endIcon?: Icon | undefined;
  error?: boolean;
  errorMessage?: string;
  font?: TypographyFont;
  fullWidth?: boolean;
  iconColor?: TextIconColor;
  isPassword?: boolean;
  maxRows?: number | undefined;
  minRows?: number | undefined;
  multiline?: boolean;
  onBlur?: (() => void) | undefined;
  onEnter?: (() => void) | undefined;
  placeholder?: string;
  prefix?: string;
  showEndIcon?: boolean;
  showFocusBottomBorder?: boolean;
  size?: TypographySize;
  startIcon?: Icon | undefined;
  startIconFocusColor?: TextIconColor;
  textWeight?: TypographyWeight;
  type?: TextFieldType;
  width?: string;
}

const TextField: React.FC<TextFieldProps> = ({
  onChange,
  type = TextFieldType.DEFAULT,
  value,
  autoFocus = false,
  avoidLayoutShift = false,
  disabled = false,
  error = false,
  errorMessage,
  maxRows,
  minRows,
  multiline = false,
  onEnter,
  fullWidth = false,
  onBlur,
  placeholder = '',
  size = TypographySize.X_SMALL,
  width,
  endIcon,
  startIcon,
  font = TypographyFont.HEADING,
  startIconFocusColor = TextIconColor.PRIMARY,
  iconColor = TextIconColor.PRIMARY,
  showEndIcon = true,
  animateStartIcon = false,
  showFocusBottomBorder = false,
  isPassword = false,
  prefix = ''
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onEnter) {
      onEnter();
      ref.current?.blur();
    }
  };

  const renderEndAdornment = () => {
    if (isFocused && value !== '' && showEndIcon) {
      return (
        <InputAdornment
          className="flex items-center"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onChange('');
          }}
          position="end"
          sx={{ cursor: 'pointer' }}
        >
          <Icons color={iconColor} icon={Icon.CLOSE} size={Size.SMALL} />
        </InputAdornment>
      );
    }
    if (endIcon) {
      return (
        <InputAdornment className="flex items-center" position="end">
          <Icons color={iconColor} icon={endIcon} />
        </InputAdornment>
      );
    }
    return null;
  };

  const { container, field, input, textArea } = textField({
    fieldType: type,
    error,
    disabled,
    disabledInputButton: disabled && type === TextFieldType.INPUT_BUTTON,
    fullWidth,
    animateStartIcon: animateStartIcon && !!startIcon && !disabled,
    showFocusBottomBorder
  });

  return (
    <div
      className={cn(
        container(),
        width,
        // Add padding when we want to avoid layout shift AND there's no error message
        avoidLayoutShift && !errorMessage ? 'pb-5' : ''
      )}
      data-testid="textfield"
      id="textfield-container"
    >
      <Typography font={font} fullWidth size={size}>
        {multiline ? (
          <TextAreaStyled
            className={cn(textArea(), field())}
            data-testid="textarea-field"
            disabled={disabled}
            maxRows={maxRows || 35}
            minRows={minRows || 2}
            onBlur={onBlur}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onChange(e.target.value)
            }
            placeholder={placeholder}
            value={value}
          />
        ) : (
          <MuiInput
            autoFocus={autoFocus}
            disabled={disabled}
            endAdornment={renderEndAdornment()}
            inputRef={ref}
            onBlur={handleBlur}
            onChange={(e) => onChange(e.target.value)}
            onFocus={handleFocus}
            placeholder={placeholder}
            slotProps={{
              root: {
                className: field()
              },
              input: {
                className: input(),
                disabled,
                onKeyDown: handleKeyDown
              }
            }}
            slots={{
              root: Root,
              input: Input
            }}
            startAdornment={
              (startIcon || prefix) && (
                <InputAdornment
                  className="flex items-center"
                  position="start"
                  sx={{
                    cursor: 'pointer'
                  }}
                >
                  {startIcon && (
                    <Icons
                      color={isFocused ? startIconFocusColor : iconColor}
                      icon={startIcon}
                    />
                  )}
                  {prefix && (
                    <Typography font={font} size={size}>
                      {prefix}
                    </Typography>
                  )}
                </InputAdornment>
              )
            }
            type={isPassword ? 'password' : 'text'}
            value={value}
          />
        )}
      </Typography>
      {errorMessage && (
        <Typography
          color={TextIconColor.DESTRUCTIVE}
          size={TypographySize.X_SMALL}
        >
          {errorMessage}
        </Typography>
      )}
    </div>
  );
};

export default TextField;
